import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { FilterCheckIcon } from "@/components/icons/FilterCheckIcon";
import { FilterIcon } from "@/components/icons/FilterIcon";
import { X } from "lucide-react";
import { formatPercentage } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { formatDuration, formatRelativeTime, formatDirectionForDisplay } from "./tableCellHelpers";

export interface ColFilterEntry {
  options: string[];
  loading: boolean;
  hasMore: boolean;
  loadedPage: number; // -1 = not loaded
  searchQuery: string;
}

export function useColumnFilterState() {
  const [state, setState] = useState<Record<string, ColFilterEntry>>({});
  const abortRefs = useRef<Record<string, AbortController>>({});
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const getEntry = useCallback(
    (col: string): ColFilterEntry =>
      state[col] ?? { options: [], loading: false, hasMore: false, loadedPage: -1, searchQuery: "" },
    [state]
  );

  const setEntry = useCallback((col: string, patch: Partial<ColFilterEntry> | ((prev: ColFilterEntry) => Partial<ColFilterEntry>)) => {
    setState((prev) => {
      const cur: ColFilterEntry = prev[col] ?? { options: [], loading: false, hasMore: false, loadedPage: -1, searchQuery: "" };
      const resolved = typeof patch === "function" ? patch(cur) : patch;
      return { ...prev, [col]: { ...cur, ...resolved } };
    });
  }, []);

  const abortCol = useCallback((col: string) => {
    abortRefs.current[col]?.abort();
    delete abortRefs.current[col];
  }, []);

  const reset = useCallback(() => {
    Object.values(abortRefs.current).forEach((ac) => ac.abort());
    abortRefs.current = {};
    Object.values(searchTimers.current).forEach(clearTimeout);
    searchTimers.current = {};
    setState({});
  }, []);

  // Abort in-flight filter requests and cancel pending search debounce timers on unmount —
  // otherwise a debounced fetch scheduled just before navigating away still fires later.
  useEffect(() => {
    return () => {
      Object.values(abortRefs.current).forEach((ac) => ac.abort());
      Object.values(searchTimers.current).forEach(clearTimeout);
    };
  }, []);

  return { getEntry, setEntry, abortCol, abortRefs, searchTimers, reset };
}

interface ColFilterPopoverProps {
  readonly columnKey: string;
  readonly selectedValues: string[];
  readonly entry: ColFilterEntry;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly onToggleValue: (col: string, val: string) => void;
  readonly onSearchChange: (col: string, q: string) => void;
  readonly onLoadMore: (col: string) => void;
  readonly onClearFilter: (col: string) => void;
  readonly onSelectAll: (col: string, vals: string[]) => void;
}

export function ColFilterPopover({
  columnKey,
  selectedValues,
  entry,
  onOpen,
  onClose,
  onToggleValue,
  onSearchChange,
  onLoadMore,
  onClearFilter,
  onSelectAll,
}: ColFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const hasActive = selectedValues.length > 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A parent-side colFilter.reset() (date/agent/queue change, or a poll detecting a real
  // rename) wipes this column's cached entry back to loadedPage=-1. `onOpen` above only
  // fires on the closed→open transition, so a popover left open through such a reset would
  // otherwise sit on stale-then-empty options forever with no refetch. Re-request only when
  // loadedPage drops from "loaded" to "not loaded" while `open` was ALREADY true across the
  // render (not just open now) — that excludes the open-transition's own internal reset
  // (handleFilterOpen/handleFilterSearchChange set loadedPage=-1 themselves right before
  // fetching), which `onOpen`/the search-debounce path already handle and must not double-fire.
  const prevOpenRef = useRef(open);
  const prevLoadedPageRef = useRef(entry.loadedPage);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (open && wasOpen && entry.loadedPage < 0 && prevLoadedPageRef.current >= 0) {
      onOpen();
    }
    prevOpenRef.current = open;
    prevLoadedPageRef.current = entry.loadedPage;
  }, [open, entry.loadedPage, onOpen]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
    if (isNearBottom && !entry.loading && entry.hasMore) {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => onLoadMore(columnKey), 100);
    }
  }, [entry.loading, entry.hasMore, onLoadMore, columnKey]);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    const handleWheelEvent = (e: WheelEvent) => {
      e.stopPropagation();
    };
    scrollContainer.addEventListener('wheel', handleWheelEvent, { passive: true });
    return () => scrollContainer.removeEventListener('wheel', handleWheelEvent);
  }, []);
  const renderOptionLabel = (val: string) => {
    if (columnKey === "sla" || columnKey === "transferredPct") {
      const num = Number(val);
      return <span className="text-xs">{formatPercentage(num)}</span>;
    }
    if (columnKey === "aht" || columnKey === "duration" || columnKey === "waitingDuration" || columnKey === "talkingDuration" || columnKey === "callDuration") {
      return <span className="text-xs">{formatDuration(Number(val))}</span>;
    }
    if (columnKey === "lastCallTaken") {
      return <span className="text-xs text-muted-foreground">{formatRelativeTime(val)}</span>;
    }
    if (columnKey === "startTime") {
      // Matches the Call Journey table's own startTime column format (convertLocalTimezone),
      // not the relative-time format used by the Users tab's "lastCallTaken" column.
      return <span className="text-xs text-muted-foreground">{convertLocalTimezone(val)}</span>;
    }
    if (columnKey === "direction") {
      const label = formatDirectionForDisplay(val);
      return (
        <TruncatedTextTooltip content={label} className="text-xs">
          {label}
        </TruncatedTextTooltip>
      );
    }
    if (columnKey === "callStatus") {
      const label = val ? val.charAt(0).toUpperCase() + val.slice(1) : val;
      return (
        <TruncatedTextTooltip content={label} className="text-xs">
          {label}
        </TruncatedTextTooltip>
      );
    }
    if (columnKey === "voicemail") {
      const normalized = val.toLowerCase();
      let label = val;
      if (normalized === "true") {
        label = "Yes";
      } else if (normalized === "false") {
        label = "No";
      }
      return <span className="text-xs">{label}</span>;
    }
    return (
      <TruncatedTextTooltip content={val} className="text-xs">
        {val}
      </TruncatedTextTooltip>
    );
  };

  const renderOptionsList = () => {
    if (entry.loading && entry.loadedPage < 0) {
      return <div className="text-xs text-gray-400 px-2 py-1">Loading...</div>;
    }
    const missingSelected = selectedValues.filter((val) => !entry.options.includes(val));
    const displayOptions = [...entry.options, ...missingSelected];
    if (displayOptions.length === 0) {
      return <div className="text-xs text-gray-400 px-2 py-1">No results</div>;
    }
    return displayOptions.map((val) => (
      <div key={val} className="flex min-w-0 items-center space-x-2">
        <input
          type="checkbox"
          id={`${columnKey}-${val}`}
          checked={selectedValues.includes(val)}
          onChange={() => onToggleValue(columnKey, val)}
          className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
        />
        <label
          htmlFor={`${columnKey}-${val}`}
          className="min-w-0 max-w-[160px] cursor-pointer text-xs leading-4"
        >
          {renderOptionLabel(val)}
        </label>
      </div>
    ));
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) onOpen();
    else onClose();
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="column-filter-trigger"
          className="h-6 w-6 p-0 ml-1 min-w-[24px] min-h-[24px] flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {hasActive ? (
            <FilterCheckIcon className="h-3 w-3" />
          ) : (
            <FilterIcon className="h-3 w-3" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[220px] max-h-[80vh] overflow-x-hidden overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2">
          <div className="mb-2">
            <p className="text-xs font-medium mb-2">Filter by values:</p>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search values..."
                className="flex-1 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500 dark:placeholder-white"
                value={entry.searchQuery}
                onChange={(e) => onSearchChange(columnKey, e.target.value)}
              />
              {entry.searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSearchChange(columnKey, "")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <div
            ref={scrollContainerRef}
            className="max-h-[150px] overflow-y-auto pr-2 overscroll-contain"
            onScroll={handleScroll}
            onWheel={handleWheel}
          >
            <div className="space-y-2">
              {renderOptionsList()}
            </div>
            {entry.hasMore && entry.loading && (
              <div className="flex items-center justify-center py-2">
                <div className="h-4 w-4 bg-gray-200 animate-pulse rounded flex-shrink-0" />
                <span className="text-xs text-gray-500 ml-2">Loading more...</span>
              </div>
            )}
            {entry.options.length > 0 && !entry.hasMore && !entry.loading && (
              <div className="text-xs text-gray-500 text-center py-2">
                All results loaded
              </div>
            )}
          </div>
          <div className="flex justify-between mt-4 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 [text-decoration:none] hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
              onClick={() => onClearFilter(columnKey)}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 [text-decoration:none] hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
              onClick={() => onSelectAll(columnKey, entry.options)}
            >
              Select All
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
